import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type UserRole = 'admin' | 'editor' | 'viewer';

// Map the wider set of role names the backend may emit (analyst, user, etc.)
// down to the three-tier UI capability model. Anything unknown defaults to
// 'viewer' (least-privilege).
function normalizeRole(raw: string | undefined | null): UserRole {
  if (!raw) return 'viewer';
  const v = String(raw).toLowerCase();
  if (v === 'admin' || v === 'owner' || v === 'super_admin') return 'admin';
  if (v === 'editor' || v === 'analyst' || v === 'operator') return 'editor';
  return 'viewer';
}

interface UserRoleContextType {
  role: UserRole;
  setRole: (role: UserRole) => void;
  /** Returns true if the current role can perform write/mutate actions */
  canWrite: boolean;
  /** Returns true if the current role is admin */
  isAdmin: boolean;
}

const UserRoleContext = createContext<UserRoleContextType | undefined>(undefined);

export const useUserRole = (): UserRoleContextType => {
  const ctx = useContext(UserRoleContext);
  if (!ctx) {
    throw new Error('useUserRole must be used within a UserRoleProvider');
  }
  return ctx;
};

/**
 * RoleGuard — conditionally renders children based on the current user role.
 *
 * @param requiredRole - minimum role required: 'viewer' (all), 'editor' (editor+admin), 'admin' (admin only)
 * @param fallback - optional fallback content when role check fails
 */
export const RoleGuard: React.FC<{
  requiredRole: UserRole;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}> = ({ requiredRole, fallback = null, children }) => {
  const { role } = useUserRole();
  const ROLE_LEVEL: Record<UserRole, number> = { viewer: 0, editor: 1, admin: 2 };
  if (ROLE_LEVEL[role] >= ROLE_LEVEL[requiredRole]) {
    return <>{children}</>;
  }
  return <>{fallback}</>;
};

export const UserRoleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Pull authoritative role from AuthContext (which sources it from the JWT
  // /auth/me payload). localStorage is only used as a hydration cache so the
  // UI doesn't flash a lower-privilege role during the auth round-trip on
  // cold load. Manual overrides via setRole still write to localStorage so
  // explicit role-switching by an admin user (Settings → role impersonation)
  // persists across reloads — but a fresh login from a different account
  // will overwrite.
  //
  // Dev vs prod default:
  //  - Production: default to 'viewer' (least-privilege). If JWT doesn't
  //    carry a role claim the user can read but not mutate — failure-safe.
  //  - Development: default to 'admin'. The dev auto-login uses a seed
  //    'admin@cybercursor.com' account; if the backend returns role='user'
  //    (which normalizes to viewer), we'd be too restrictive for the
  //    developer workflow that previously worked without role plumbing.
  const auth = useAuth();
  const _isProd = process.env.NODE_ENV === 'production';
  const _devDefaultRole: UserRole = _isProd ? 'viewer' : 'admin';
  const [role, setRoleState] = useState<UserRole>(() => {
    const stored = localStorage.getItem('user_role');
    if (stored === 'admin' || stored === 'editor' || stored === 'viewer') return stored;
    return _devDefaultRole;
  });

  // Sync role from authenticated user whenever the auth state changes.
  // In dev mode, accept whatever the backend returns — including the
  // permissive 'admin' default — but never DOWNGRADE a stored admin role
  // to viewer just because the backend response shape doesn't include
  // role for the seed account.
  useEffect(() => {
    if (auth.user?.role) {
      const next = normalizeRole(auth.user.role);
      // Dev: only upgrade (viewer → editor → admin). Don't downgrade an
      // implicit dev-admin to viewer from a missing role claim.
      if (!_isProd && next === 'viewer' && role !== 'viewer') return;
      // Persist so a stale cached role (e.g. a previous 'editor') doesn't
      // flash on the next cold load before this effect re-runs.
      localStorage.setItem('user_role', next);
      setRoleState(prev => (prev === next ? prev : next));
    }
  }, [auth.user?.role, _isProd, role]);

  const setRole = useCallback((newRole: UserRole) => {
    setRoleState(newRole);
    localStorage.setItem('user_role', newRole);
  }, []);

  const canWrite = role !== 'viewer';
  const isAdmin = role === 'admin';

  return (
    <UserRoleContext.Provider value={{ role, setRole, canWrite, isAdmin }}>
      {children}
    </UserRoleContext.Provider>
  );
};
